import { map, pick, update } from 'lodash/fp';
import { replaceSubArray } from '@mews-commander/utils';
import {
    ResourceCategoryTypeOccupancy,
    ResourceCategoryOccupancy,
    AttributePrice,
    AsidePricing,
    BodyPricing,
    BasePricing,
    Occupancy,
    Price,
    BasePrice,
} from '../types';

const filterChildren =
    <T extends BasePricing>(id: string | null) =>
    (data: T[]) =>
        data.filter((pricing) => pricing.baseId === id);

const makeTree = <T extends BasePricing>(data: T[], parentId: string | null = null): T[] => {
    const nodes = filterChildren<T>(parentId)(data);
    nodes.forEach((node) => (node.subAttributePricings = makeTree(data, node.id)));
    return nodes;
};

const mapPricingAside = map(pick(['baseId', 'absoluteAdjustment', 'relativeAdjustment', 'id'])) as unknown as (
    data: Array<AttributePrice>
) => AsidePricing[];

const mapPricingBody = map(pick(['baseId', 'prices', 'id'])) as unknown as (
    data: Array<AttributePrice>
) => BodyPricing[];

export const transformPricing = (data: Array<AttributePrice>) => ({
    aside: makeTree(mapPricingAside(data)),
    body: makeTree(mapPricingBody(data)),
});

const mapOccupancyAside = map<
    ResourceCategoryTypeOccupancy,
    { resourceCategoryType: string; resourceCategoryIds: string[] }
>(({ resourceCategoryType, categoryOccupancies }) => ({
    resourceCategoryType,
    resourceCategoryIds: map('resourceCategoryId', categoryOccupancies),
}));

const mapOccupancyBody = map<
    ResourceCategoryTypeOccupancy,
    { occupancies: Array<Occupancy>; categoryOccupancies: Array<{ occupancies: Array<Occupancy> }> }
>(({ resourceCategoryType, occupancies, categoryOccupancies }) => ({
    resourceCategoryType,
    occupancies,
    categoryOccupancies: map(
        pick<ResourceCategoryOccupancy, 'occupancies' | 'resourceCategoryId'>(['occupancies', 'resourceCategoryId']),
        categoryOccupancies
    ),
}));

export const transformOccupancy = (data: Array<ResourceCategoryTypeOccupancy>) => ({
    aside: mapOccupancyAside(data),
    body: mapOccupancyBody(data),
});

export const updatePrices = (
    startIndex: number,
    newPrices: Array<Price>,
    initialPricing: BasePrice | AttributePrice
): BasePrice | AttributePrice =>
    update('prices', (initialPrices) => replaceSubArray(startIndex, newPrices, initialPrices), initialPricing);
